import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { API } from "../auth/AuthContext";
import { API_REQUEST_TIMEOUT_MS } from "../auth/authRequest";
import ProfileLayout from "../components/ProfileLayout";
import { Star, ChevronDown, ChevronUp, ClipboardCheck, Car, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import PolicyCard from "../components/PolicyCard";
import TelematicsPanel from "../components/TelematicsPanel";
import TripInspectionPanel from "../components/TripInspectionPanel";
import TripMessagesPanel from "../components/TripMessagesPanel";
import AmendRequestForm from "../components/AmendRequestForm";
import ReportIssueModal from "../components/ReportIssueModal";
import CancellationReviewDialog from "../components/CancellationReviewDialog";
import { formatBookingStatus, bookingStatusDotClass } from "../lib/bookingStatus";
import { formatBookingError } from "../lib/bookingErrors";
import { formatUsd } from "../lib/currencyDisplay";
import { bookingTimeZoneLabel, formatTripDateLabel, formatTripRange } from "../lib/dateLocal";
import { pageItems } from "../lib/pageItems";

function ReviewModal({ booking, onClose, onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const submit = async () => {
    try {
      await axios.post(`${API}/bookings/${booking.booking_id}/review`, { rating, comment }, { withCredentials: true });
      toast.success("Review posted"); onSubmitted();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border border-[var(--zc-border)]" data-testid="profile-review-modal">
        <DialogHeader><DialogTitle className="zc-headline text-2xl">Review your trip</DialogTitle></DialogHeader>
        <div className="text-sm text-[var(--zc-text-2)]">{booking?.vehicle_snapshot?.year} {booking?.vehicle_snapshot?.make} {booking?.vehicle_snapshot?.model}</div>
        <div className="flex items-center gap-2 mt-3">
          {[1,2,3,4,5].map(n => (
            <button key={n} onClick={() => setRating(n)}>
              <Star size={22} className={n <= rating ? "text-[var(--zc-accent)]" : "text-[var(--zc-border-2)]"} fill={n <= rating ? "currentColor" : "none"} />
            </button>
          ))}
        </div>
        <textarea value={comment} onChange={e => setComment(e.target.value)} className="zc-input w-full p-3 text-sm mt-4 h-24" placeholder="Share your experience…" />
        <div className="flex justify-end gap-2 mt-2">
          <button onClick={onClose} className="px-4 h-10 zc-btn-ghost text-sm">Cancel</button>
          <button onClick={submit} className="px-4 h-10 zc-btn-primary text-sm">Post review</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function tabPillClass(active) {
  return (
    "px-3 h-8 rounded-full text-xs font-semibold uppercase tracking-widest transition inline-flex items-center gap-1.5 " +
    (active
      ? "bg-[var(--zc-accent)] text-white"
      : "bg-[var(--zc-bg-3)] text-[var(--zc-text-2)] hover:text-[var(--zc-text)]")
  );
}

function tripAction(booking) {
  if (booking.external_partner) return null;
  if (booking.status === "confirmed" && !booking.pickup_inspection_id) {
    return { label: "Start pickup scan", hint: "Required before you drive", kind: "pickup" };
  }
  if (booking.status === "active" && !booking.return_inspection_id) {
    return { label: "Return car & scan", hint: "Document condition before handoff", kind: "return" };
  }
  if (booking.status === "active" && booking.return_inspection_id) {
    return { label: "Awaiting host close-out", hint: "Return scan done — host will complete the trip", kind: "waiting" };
  }
  return null;
}

export default function ProfileBookings() {
  const [bookings, setBookings] = useState([]);
  const [parkingBookings, setParkingBookings] = useState([]);
  const [reviewing, setReviewing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [amendBookingId, setAmendBookingId] = useState(null);
  const [reportBooking, setReportBooking] = useState(null);
  const [cancellingBooking, setCancellingBooking] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const supplementHandled = useRef(false);
  const tab = searchParams.get("tab") === "parking" ? "parking" : "cars";
  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === "cars") next.delete("tab"); else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  const load = async () => {
    const r = await axios.get(`${API}/bookings/mine`, { withCredentials: true });
    setBookings(pageItems(r.data));
  };
  const loadParking = async () => {
    const r = await axios.get(`${API}/parking-bookings/mine`, { withCredentials: true });
    setParkingBookings(pageItems(r.data));
  };
  useEffect(() => { load(); loadParking(); }, []);

  useEffect(() => {
    if (supplementHandled.current) return;
    if (searchParams.get("supplement") !== "1") return;
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    supplementHandled.current = true;

    const bookingId = searchParams.get("booking_id");
    const cleanSupplementParams = () => {
      const next = new URLSearchParams(searchParams);
      next.delete("supplement");
      next.delete("session_id");
      next.delete("booking_id");
      setSearchParams(next, { replace: true });
    };

    (async () => {
      try {
        await axios.post(
          `${API}/payments/stripe/complete`,
          { session_id: sessionId },
          { withCredentials: true, timeout: API_REQUEST_TIMEOUT_MS },
        );
        toast.success("Supplement payment confirmed — your trip change is applied");
        await load();
        if (bookingId) {
          setExpanded(bookingId);
          requestAnimationFrame(() => {
            document.getElementById(`pb-${bookingId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
          });
        }
      } catch (e) {
        toast.error(formatBookingError(e.response?.data?.detail) || "Payment confirmation failed");
      } finally {
        cleanSupplementParams();
      }
    })();
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const bid = searchParams.get("booking");
    if (!bid || bookings.length === 0) return;
    if (bookings.some(b => b.booking_id === bid)) {
      setExpanded(bid);
      requestAnimationFrame(() => {
        document.getElementById(`pb-${bid}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [searchParams, bookings]);

  const pastVehicles = useMemo(() => {
    const pastStatuses = new Set(["completed", "cancelled", "expired"]);
    const byVehicle = new Map();
    bookings.forEach(b => {
      if (!b.vehicle_id || !pastStatuses.has(b.status)) return;
      const prev = byVehicle.get(b.vehicle_id);
      if (!prev || (b.start_date || "") > (prev.last_trip || "")) {
        byVehicle.set(b.vehicle_id, { ...b.vehicle_snapshot, vehicle_id: b.vehicle_id, last_trip: b.start_date });
      }
    });
    return [...byVehicle.values()];
  }, [bookings]);

  const cancel = async (id) => {
    if (!window.confirm("Cancel this booking?")) return;
    try {
      await axios.post(`${API}/bookings/${id}/cancel`, {}, { withCredentials: true });
      toast.success("Cancelled"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cancelParking = async (id) => {
    if (!window.confirm("Cancel this parking booking?")) return;
    try {
      await axios.post(`${API}/parking-bookings/${id}/cancel`, {}, { withCredentials: true });
      toast.success("Cancelled"); loadParking();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <ProfileLayout eyebrow="Profile" title="My bookings">
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="bookings-tabs">
        <button type="button" onClick={() => setTab("cars")} className={tabPillClass(tab === "cars")} data-testid="bookings-tab-cars">
          <Car size={13} /> Cars ({bookings.length})
        </button>
        <button type="button" onClick={() => setTab("parking")} className={tabPillClass(tab === "parking")} data-testid="bookings-tab-parking">
          <MapPin size={13} /> Parking ({parkingBookings.length})
        </button>
      </div>

      {tab === "cars" && (
      <>
      <div className="zc-card overflow-hidden overflow-x-auto zc-stack-table" data-testid="profile-bookings-table">
        <div className="hidden md:grid md:grid-cols-[1.5fr,1fr,1fr,1fr,1fr] zc-stack-head px-4 sm:px-6 py-3 text-xs uppercase tracking-widest text-[var(--zc-text-3)] border-b border-[var(--zc-border)] font-semibold">
          <div>Vehicle</div><div>Dates</div><div>Total</div><div>Status</div><div></div>
        </div>
        {bookings.length === 0 && <div className="px-6 py-12 text-center text-[var(--zc-text-3)] text-sm">No bookings yet.</div>}
        {bookings.map(b => {
          const isOpen = expanded === b.booking_id;
          const canManage = b.status === "confirmed" || b.status === "active";
          const canCancel = b.status === "confirmed";
          const isPartner = !!b.external_partner;
          const amendPolicy = b.amendment_policy || b.vehicle_snapshot?.amendment_policy || {};
          const canRequestAmendment = amendPolicy.allows_return_date_extension === true
            || (amendPolicy.allows_return_address_change === true && amendPolicy.allows_one_way === true);
          const action = tripAction(b);
          const openManage = () => setExpanded(isOpen ? null : b.booking_id);
          return (
          <div key={b.booking_id} id={`pb-${b.booking_id}`} className="border-b border-[var(--zc-border)] last:border-0" data-testid={`pb-${b.booking_id}`}>
            <div className="zc-stack-row md:grid md:grid-cols-[1.5fr,1fr,1fr,1fr,1fr] md:items-center px-4 sm:px-6 py-4 text-sm">
              <div data-label="Vehicle" className="flex items-center gap-3 min-w-0">
                {b.vehicle_snapshot?.image && <img src={b.vehicle_snapshot.image} className="w-14 h-10 object-cover rounded shrink-0" alt="" />}
                <div className="min-w-0">
                  <div className="font-semibold flex items-center gap-2 flex-wrap">
                    {b.vehicle_snapshot?.year} {b.vehicle_snapshot?.make} {b.vehicle_snapshot?.model}
                    {b.external_partner && (
                      <span className="zc-badge px-1.5 py-0.5 text-white" style={{ background: "rgba(15,15,18,0.85)" }} title={`Partner reservation via ${b.external_partner}`}>
                        Partner
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--zc-text-3)]">{b.vehicle_snapshot?.city}</div>
                </div>
              </div>
              <div data-label="Dates" className="text-[var(--zc-text-2)] break-words">
                <div>{formatTripRange(b.start_date, b.end_date) || "—"}</div>
                {bookingTimeZoneLabel(b) && (
                  <div className="text-2xs text-[var(--zc-text-3)] mt-1">
                    {bookingTimeZoneLabel(b)}
                  </div>
                )}
              </div>
              <div data-label="Total" className="font-semibold">${formatUsd(b.total)}</div>
              <div data-label="Status">
                <div className="flex items-center gap-2">
                  <span className={"dot " + bookingStatusDotClass(b.status)} />
                  {formatBookingStatus(b)}
                </div>
                {action?.hint && (
                  <div className={"text-2xs mt-0.5 normal-case " + (action.kind === "return" ? "text-[var(--zc-accent)]" : "text-[var(--zc-text-3)]")}>
                    {action.hint}
                  </div>
                )}
              </div>
              <div data-label="Actions" className="zc-stack-actions flex justify-start md:justify-end gap-2 items-center flex-wrap">
                {action?.kind === "pickup" && (
                  <button
                    type="button"
                    onClick={openManage}
                    className="text-xs zc-btn-primary px-3 h-8 inline-flex items-center gap-1"
                    data-testid={`pb-pickup-scan-${b.booking_id}`}
                  >
                    <ClipboardCheck size={12} /> {action.label}
                  </button>
                )}
                {action?.kind === "return" && (
                  <button
                    type="button"
                    onClick={openManage}
                    className="text-xs zc-btn-primary px-3 h-8 inline-flex items-center gap-1"
                    data-testid={`pb-return-scan-${b.booking_id}`}
                  >
                    <ClipboardCheck size={12} /> {action.label}
                  </button>
                )}
                {canManage && (
                  <button
                    className="text-xs flex items-center gap-1 text-[var(--zc-text-2)] hover:text-[var(--zc-text)]"
                    onClick={openManage}
                    data-testid={`pb-manage-${b.booking_id}`}
                  >
                    Manage {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                )}
                {canCancel && (
                  <div className="flex flex-col items-start md:items-end gap-0.5">
                    <button
                      className="text-xs text-[var(--zc-text-2)] hover:text-[var(--zc-text)]"
                      onClick={() => setCancellingBooking(b)}
                      data-testid={`pb-cancel-${b.booking_id}`}
                    >
                      Cancel
                    </button>
                    {isPartner && (
                      <span className="text-2xs text-[var(--zc-text-3)] max-w-[12rem] text-left md:text-right" data-testid={`pb-partner-cancel-hint-${b.booking_id}`}>
                        Partner reservation — Z-Cars refund policy applies; supplier notified separately.
                      </span>
                    )}
                  </div>
                )}
                {canManage && !b.external_partner && (
                  canRequestAmendment ? (
                    <button
                      type="button"
                      className="text-xs text-[var(--zc-accent)] font-semibold"
                      onClick={() => {
                        setAmendBookingId(amendBookingId === b.booking_id ? null : b.booking_id);
                        if (expanded !== b.booking_id) setExpanded(b.booking_id);
                      }}
                      data-testid={`pb-amend-toggle-${b.booking_id}`}
                    >
                      {amendBookingId === b.booking_id ? "Hide change form" : "Request change"}
                    </button>
                  ) : (
                    <span className="text-2xs text-[var(--zc-text-3)]" data-testid={`pb-amend-unavailable-${b.booking_id}`}>
                      Changes not offered by host
                    </span>
                  )
                )}
                {b.status === "completed" && !b.review_left && (
                  <button className="text-xs zc-btn-primary px-3 h-8" onClick={() => setReviewing(b)}>Review</button>
                )}
                <button
                  type="button"
                  className="text-xs text-[var(--zc-text-2)] hover:text-[var(--zc-text)]"
                  onClick={() => setReportBooking(b)}
                  data-testid={`pb-report-issue-${b.booking_id}`}
                >
                  Report issue
                </button>
              </div>
            </div>
            {isOpen && canManage && (
              <div className="px-6 pb-5 grid md:grid-cols-2 gap-4 bg-[var(--zc-bg-2)]" data-testid={`pb-manage-panel-${b.booking_id}`}>
                {!isPartner && (
                  <>
                    <TripInspectionPanel booking={b} onDone={() => load()} />
                    <TelematicsPanel bookingId={b.booking_id} isRenter status={b.status} pickupDone={!!b.pickup_inspection_id} />
                  </>
                )}
                <PolicyCard bookingId={b.booking_id} />
                {!isPartner && <TripMessagesPanel bookingId={b.booking_id} compact />}
                {isPartner && (
                  <div className="md:col-span-2 p-4 rounded-xl bg-[var(--zc-bg-3)] text-sm text-[var(--zc-text-2)]" data-testid={`pb-partner-manage-note-${b.booking_id}`}>
                    This is a partner reservation. Pickup scans and in-app trip changes are not available — contact support if you need help before pick-up.
                  </div>
                )}
                {amendBookingId === b.booking_id && !isPartner && (
                  <div className="md:col-span-2">
                    <AmendRequestForm
                      booking={b}
                      onDone={() => { setAmendBookingId(null); load(); }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );})}
      </div>

      <div id="past-vehicles" className="mt-12">
        <div className="eyebrow mb-3">Past vehicles</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="profile-past-vehicles">
          {pastVehicles.length === 0 && (
            <div className="zc-card p-10 text-center text-[var(--zc-text-3)] text-sm col-span-full">
              <Car size={28} className="mx-auto mb-3 opacity-50" />
              You haven&apos;t rented any cars yet.
              <div className="mt-4"><Link to="/browse" className="zc-btn-primary px-4 h-9 inline-grid place-items-center text-xs">Browse the fleet</Link></div>
            </div>
          )}
          {pastVehicles.map(v => (
            <Link key={v.vehicle_id} to={`/vehicles/${v.vehicle_id}`} className="zc-card overflow-hidden block">
              <div className="aspect-[16/10] bg-[var(--zc-bg-3)]">
                {v.image && <img src={v.image} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="p-5">
                <div className="zc-title text-lg">{v.year} {v.make} {v.model}</div>
                <div className="text-xs text-[var(--zc-text-3)] mt-1">{v.city} · last trip {formatTripDateLabel(v.last_trip)}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      </>
      )}

      {tab === "parking" && (
      <div className="zc-card overflow-hidden overflow-x-auto zc-stack-table" data-testid="profile-parking-table">
        <div className="hidden md:grid md:grid-cols-[1.5fr,1.2fr,0.8fr,1fr,1fr,1fr] zc-stack-head px-4 sm:px-6 py-3 text-xs uppercase tracking-widest text-[var(--zc-text-3)] border-b border-[var(--zc-border)] font-semibold">
          <div>Spot</div><div>Window</div><div>Hours</div><div>Total</div><div>Status</div><div></div>
        </div>
        {parkingBookings.length === 0 && <div className="px-6 py-12 text-center text-[var(--zc-text-3)] text-sm">No parking bookings yet.</div>}
        {parkingBookings.map(b => (
          <div key={b.booking_id} className="zc-stack-row md:grid md:grid-cols-[1.5fr,1.2fr,0.8fr,1fr,1fr,1fr] md:items-center px-4 sm:px-6 py-4 border-b border-[var(--zc-border)] last:border-0 text-sm" data-testid={`pp-${b.booking_id}`}>
            <div data-label="Spot" className="flex items-center gap-3 min-w-0">
              {b.spot_snapshot?.image && <img src={b.spot_snapshot.image} className="w-14 h-10 object-cover rounded shrink-0" alt="" />}
              <div className="min-w-0">
                <div className="font-semibold line-clamp-1">{b.spot_snapshot?.name}</div>
                <div className="text-xs text-[var(--zc-text-3)]">{b.spot_snapshot?.city}</div>
              </div>
            </div>
            <div data-label="Window" className="text-[var(--zc-text-2)] text-xs break-words">{formatTripRange(b.check_in, b.check_out) || "—"}</div>
            <div data-label="Hours">{b.hours}h</div>
            <div data-label="Total" className="font-semibold">${formatUsd(b.total)}</div>
            <div data-label="Status" className="flex items-center gap-2">
              <span className={"dot " + bookingStatusDotClass(b.status)} />
              {formatBookingStatus(b)}
            </div>
            <div data-label="Actions" className="zc-stack-actions flex justify-start md:justify-end">
              {(b.status === "confirmed" || b.status === "active") && (
                <button className="text-xs text-[var(--zc-text-2)] hover:text-[var(--zc-text)]" onClick={() => cancelParking(b.booking_id)}>Cancel</button>
              )}
            </div>
          </div>
        ))}
      </div>
      )}

      {reviewing && <ReviewModal booking={reviewing} onClose={() => setReviewing(null)} onSubmitted={() => { setReviewing(null); load(); }} />}
      <CancellationReviewDialog
        booking={cancellingBooking}
        onClose={() => setCancellingBooking(null)}
        onCancelled={() => {
          setCancellingBooking(null);
          load();
        }}
      />
      <ReportIssueModal
        booking={reportBooking}
        open={!!reportBooking}
        onClose={() => setReportBooking(null)}
      />
    </ProfileLayout>
  );
}
